import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { EditableComment, EditableSeries } from './types';

const PREFIX = 'KPIInputForm';

const classes = {
  input: `${PREFIX}-input`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`& .${classes.input}`]: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    '& .MuiInputLabel-root': {
      lineHeight: '1.6rem',
      whiteSpace: 'initial'
    }
  }
}));

interface KPIInputFormProps {
  handleEdit?: (values: EditableSeries[], comment: EditableComment) => void;
  comments?: EditableComment;
  formData: EditableSeries[];
  title: string;
}

const initialComment = {
  enabled: false,
  changed: false,
  inputType: '',
  value: '',
  commentSeries: ''
};

const KPIInputForm = ({
  title,
  handleEdit = () => null,
  comments,
  formData
}: KPIInputFormProps) => {
  const [values, setValues] = React.useState<EditableSeries[]>([]);
  const [comment, setComment] = React.useState<EditableComment>(initialComment);

  const handleValueChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = values.map((item: EditableSeries) => {
      if (item.name !== name) {
        // This isn't the item we care about - keep it as-is
        return item;
      }
      // Otherwise, this is the one we want - return an updated value
      return {
        ...item,
        value: event.target.value,
        changed: true
      };
    });

    setValues(newValues);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment({ ...comment, value: event.target.value, changed: true });
  };

  useEffect(() => {
    const initData = async () => {
      setValues(formData);
      if (comments && comments.enabled) setComment(comments);
    };
    initData();
  }, [formData]);

  const submitAllowed = values.some((value: EditableSeries) => value.changed) || comment?.changed;

  return (
    <StyledCard style={{ minHeight: '400px' }} data-testid="KPIInputForm-container">
      {values.length > 0 && comment ? (
        <>
          <CardContent>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Divider />
            <form noValidate autoComplete="off" className={classes.input}>
              {values.map((value: EditableSeries) => (
                <div key={value.name}>
                  <TextField
                    data-testid="KPIInputForm-value-field"
                    inputProps={{ 'data-testid': 'KPIInputForm-value-input' }}
                    className={classes.input}
                    id={`value-input-${value.name}`}
                    label={`Value for ${value.displayName || value.name}${
                      value.unitName === 'Date' ? ' (yyyy-mm-dd)' : ''
                    }`}
                    disabled={value.inputType !== '0'}
                    helperText={
                      value.inputType !== '0' && 'Manual input is not allowed for this series.'
                    }
                    value={value.value || ''}
                    required
                    placeholder="Insert value here"
                    onChange={handleValueChange(value.name)}
                    margin="normal"
                    InputLabelProps={{
                      shrink: true
                    }}
                    variant="outlined"
                  />
                </div>
              ))}
              {comments?.enabled && (
                <div>
                  <TextField
                    data-testid="KPIInputForm-comment-field"
                    inputProps={{ 'data-testid': 'KPIInputForm-comment-input' }}
                    className={classes.input}
                    value={comment.value || ''}
                    helperText={
                      comments?.inputType !== '0'
                        ? 'Manual input is not allowed for this series.'
                        : ''
                    }
                    disabled={comments?.inputType !== '0'}
                    onChange={handleCommentChange}
                    fullWidth
                    id="comment-input"
                    label="Comments"
                    multiline
                    rows={4}
                    placeholder="Insert analysis comments here"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </div>
              )}
            </form>
            <Button
              data-testid="KPIInputForm-submit-button"
              size="small"
              disabled={!submitAllowed}
              variant="contained"
              onClick={() => handleEdit(values, comment)}
            >
              Submit
            </Button>
          </CardContent>
        </>
      ) : (
        <Typography data-testid="KPIInputForm-no-selected">Select KPI from the list</Typography>
      )}
    </StyledCard>
  );
};

export default KPIInputForm;
